import { useEffect, useRef } from "react";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchOrganizationByReference } from "../services/organizationService";

/**
 * Extract all FHIR references from a resource (recursively)
 */
function extractAllReferences(resource) {
  const results = new Set();

  function walk(obj) {
    if (!obj || typeof obj !== "object") return;

    // Reference object
    if (obj.reference && typeof obj.reference === "string") {
      results.add(obj.reference);
    }

    // Inline full resource
    if (obj.resourceType && obj.id) {
      results.add(`${obj.resourceType}/${obj.id}`);
    }

    Object.values(obj).forEach((v) => walk(v));
  }

  walk(resource);
  return Array.from(results);
}

/**
 * React hook to resolve all referenced resources.
 */
export function useResolveReferences(resource, fhirClient, store) {
  const lastRefs = useRef([]);

  useEffect(() => {
    if (!resource || !fhirClient) return;

    const refs = extractAllReferences(resource);

    // Prevent refetching if no changes
    if (JSON.stringify(refs) === JSON.stringify(lastRefs.current)) return;
    lastRefs.current = refs;

    async function resolve() {
      for (const ref of refs) {
        try {
          if (ref.startsWith("Patient/")) {
            await fetchPatientByReference(ref, fhirClient, store);
          } else if (ref.startsWith("Practitioner/")) {
            await fetchProviderByReference(ref, fhirClient, store);
          } else if (ref.startsWith("Organization/")) {
            console.log("Fetching org", ref);
            await fetchOrganizationByReference(ref, fhirClient, store);
          }
        } catch (err) {
          console.error("Failed to resolve ref", ref, err);
        }
      }
    }

    resolve();
  }, [resource, fhirClient]); // ❗ Do NOT include "store"
}
